import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Affix,
  Alert,
  Button,
  Card,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  CameraOutlined,
  CloudDownloadOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  LockOutlined,
  PictureOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SaveOutlined,
  StarFilled,
  StarOutlined,
  UndoOutlined,
  UnlockOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  ReplaceStandingsInput,
  StandingRowInput,
  useReplaceStandings,
  useStandings,
  useStandingsScreenshotPreview,
  useSyncStandings,
} from '@shared/api/hooks/useStandings';
import { useBranding } from '@/theme/useBranding';
import { MediaSelector } from '@shared/components/MediaSelector';
import { DraftRow, useDirtyBuffer } from './hooks/useDirtyBuffer';
import { QuickResultPopover } from './components/QuickResultPopover';
import { ShortcutsHelpModal } from './components/ShortcutsHelpModal';
import {
  TieBreakMode,
  findTiedGroups,
  loadTieBreakMode,
  persistTieBreakMode,
  sortStandings,
} from './sort';

/**
 * T-285: legenda zone classifica (Serie D girone F). Colori richiesti dalla
 * societa'. La legenda dell'APP mobile NON e' qui (vive in mobile-app, gestita
 * da T-286): questa e' solo la legenda del backoffice.
 */
const STANDINGS_LEGEND: { color: string; label: string }[] = [
  { color: '#722ed1', label: 'Promozione in Serie C' },
  { color: '#faad14', label: 'Playoff' },
  { color: '#001d66', label: 'Playout' },
  { color: '#cf1322', label: 'Retrocessione in Eccellenza' },
];

/** "Dati aggiornati al DD/MM/YYYY alle ore HH:MM" (ora italiana). */
function formatLastSynced(iso: string | null): string {
  if (!iso) return 'Mai aggiornata da fonti esterne';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Mai aggiornata da fonti esterne';
  const date = d.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Rome',
  });
  const time = d.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Rome',
  });
  return `Dati aggiornati al ${date} alle ore ${time}`;
}

function syncErrorMessage(error: unknown): string {
  const data = (
    error as {
      response?: { data?: { error?: string; message?: string | string[] } };
    }
  )?.response?.data;
  // T-285 fix (opzione A): per i fallimenti "colpa della fonte" il backend torna
  // un 424 con la causa gia' scritta e PULITA in `message` (es. "...non ha risposto
  // entro 10 secondi", "...HTTP 403", "...ricevute N squadre") e il codice in `error`
  // (source_unreachable | source_bad_response). Il filtro R6 lascia passare quei due.
  // Basta mostrare il message. Per un errore server vero (>=500) il filtro lo maschera
  // in "Internal server error": in quel caso diamo un testo generico all'admin.
  const msg = data?.message;
  const text = Array.isArray(msg) ? msg.join('; ') : msg ?? (error as Error)?.message;
  if (text && text !== 'Internal server error') return text;
  return 'Aggiornamento da fonti esterne fallito';
}

type ColKey = 'played' | 'won' | 'drawn' | 'lost' | 'goalsFor' | 'goalsAgainst' | 'penalty' | 'points';

const NUMERIC_COLS: ColKey[] = ['played', 'won', 'drawn', 'lost', 'goalsFor', 'goalsAgainst', 'penalty', 'points'];

function editableColsFor(row: DraftRow): ColKey[] {
  return row.pointsLocked
    ? NUMERIC_COLS
    : NUMERIC_COLS.filter((c) => c !== 'points');
}

interface FocusCell {
  uid: string;
  col: ColKey;
}

function nextFocus(
  cur: FocusCell,
  dir: 'right' | 'left' | 'down' | 'up',
  rows: DraftRow[],
): FocusCell | null {
  const idx = rows.findIndex((r) => r.uid === cur.uid);
  if (idx === -1) return null;
  const row = rows[idx];
  const cols = editableColsFor(row);
  const cIdx = cols.indexOf(cur.col);
  if (dir === 'up') {
    if (idx === 0) return cur;
    const prev = rows[idx - 1];
    const prevCols = editableColsFor(prev);
    const col = prevCols.includes(cur.col) ? cur.col : prevCols[prevCols.length - 1];
    return { uid: prev.uid, col };
  }
  if (dir === 'down') {
    if (idx === rows.length - 1) return cur;
    const next = rows[idx + 1];
    const nextCols = editableColsFor(next);
    const col = nextCols.includes(cur.col) ? cur.col : nextCols[nextCols.length - 1];
    return { uid: next.uid, col };
  }
  if (dir === 'right') {
    if (cIdx < cols.length - 1) return { uid: cur.uid, col: cols[cIdx + 1] };
    if (idx < rows.length - 1) {
      const next = rows[idx + 1];
      return { uid: next.uid, col: editableColsFor(next)[0] };
    }
    return cur;
  }
  if (cIdx > 0) return { uid: cur.uid, col: cols[cIdx - 1] };
  if (idx > 0) {
    const prev = rows[idx - 1];
    const prevCols = editableColsFor(prev);
    return { uid: prev.uid, col: prevCols[prevCols.length - 1] };
  }
  return cur;
}

function toReplacePayload(rows: DraftRow[]): ReplaceStandingsInput {
  const live = rows.filter((r) => !r.isDeleted);
  const payload: StandingRowInput[] = live.map((r, i) => ({
    position: i + 1,
    teamName: r.teamName.trim(),
    teamLogo: r.teamLogo ?? undefined,
    played: r.played,
    won: r.won,
    drawn: r.drawn,
    lost: r.lost,
    goalsFor: r.goalsFor,
    goalsAgainst: r.goalsAgainst,
    goalDifference: r.goalDifference,
    points: r.points,
    penalty: r.penalty,
    isOwnTeam: r.isOwnTeam,
  }));
  // T-285: stagione/competizione risolte dal backend (stagione corrente).
  return { rows: payload };
}

export function StandingsPage(): React.ReactElement {
  const { clubName } = useBranding();
  const { data: response, isLoading, refetch, isFetching } = useStandings();
  const rows = response?.data;
  // Stagione/competizione correnti: unica sorgente = backend (T-285).
  const season = response?.season ?? '';
  const competition = response?.competition ?? '';
  const lastSyncedAt = response?.lastSyncedAt ?? null;
  const compSeasonKey = `${competition}__${season}`;

  const [tieBreakMode, setTieBreakModeState] = useState<TieBreakMode>('simple');
  const [focus, setFocus] = useState<FocusCell | null>(null);
  const [editing, setEditing] = useState<FocusCell | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [logoForUid, setLogoForUid] = useState<string | null>(null);
  const [quickAnchorUid, setQuickAnchorUid] = useState<string | null>(null);
  const [discardConfirm, setDiscardConfirm] = useState(false);

  const replaceMutation = useReplaceStandings();
  const syncMutation = useSyncStandings();
  const screenshotMutation = useStandingsScreenshotPreview();
  const buffer = useDirtyBuffer(rows);
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [screenshotWarnings, setScreenshotWarnings] = useState<string[]>([]);

  // Ripiego screenshot: la vision legge la tabella, le righe vanno in BOZZA (l'admin controlla e salva).
  const onScreenshot = useCallback(
    async (file: File) => {
      try {
        const res = await screenshotMutation.mutateAsync(file);
        // La vision non vede i loghi: tieni quelli attuali quando il nome coincide.
        const logos = new Map(buffer.visible.map((r) => [r.teamName.trim().toLowerCase(), r.teamLogo]));
        buffer.replaceAll(
          res.data.map((r) => ({
            ...r,
            teamLogo: logos.get(r.teamName.toLowerCase()) ?? null,
            penalty: r.penalty ?? 0,
            isOwnTeam: r.isOwnTeam ?? false,
          })),
        );
        setScreenshotWarnings(res.warnings);
        setScreenshotOpen(false);
        message.success(`Lette ${res.data.length} squadre dallo screenshot. Controlla e premi Salva.`);
      } catch (e) {
        message.error(syncErrorMessage(e));
      }
    },
    [buffer, screenshotMutation],
  );

  // Re-load tie-break preference whenever competition+season changes.
  useEffect(() => {
    setTieBreakModeState(loadTieBreakMode(compSeasonKey));
  }, [compSeasonKey]);

  const setTieBreakMode = useCallback(
    (m: TieBreakMode) => {
      setTieBreakModeState(m);
      persistTieBreakMode(compSeasonKey, m);
    },
    [compSeasonKey],
  );

  // Sort visible rows for display. The persisted `position` is recomputed at save time.
  const sortedRows: DraftRow[] = useMemo(
    () => sortStandings(buffer.visible, tieBreakMode),
    [buffer.visible, tieBreakMode],
  );

  const tiedGroups = useMemo(() => findTiedGroups(sortedRows, tieBreakMode), [sortedRows, tieBreakMode]);
  const tiedRowUids = useMemo(() => {
    const s = new Set<string>();
    for (const g of tiedGroups) for (const r of g) s.add(r.uid);
    return s;
  }, [tiedGroups]);

  const tenantRow = useMemo(() => buffer.draft.find((r) => r.isOwnTeam && !r.isDeleted) ?? null, [buffer.draft]);

  // Lookup draft by uid.
  const draftByUid = useMemo(() => {
    const m = new Map<string, DraftRow>();
    for (const r of buffer.draft) m.set(r.uid, r);
    return m;
  }, [buffer.draft]);

  const quickAnchorDraft = quickAnchorUid ? draftByUid.get(quickAnchorUid) ?? null : null;

  // --- Save / discard ---
  const onSave = useCallback(async () => {
    if (!buffer.isDirty) return;
    const live = buffer.draft.filter((r) => !r.isDeleted);
    if (live.length === 0) {
      message.warning("La classifica non puo' essere vuota.");
      return;
    }
    // Sort by current tie-break so the persisted `position` matches what the operator sees.
    const sortedLive = sortStandings(live, tieBreakMode);
    // Validate: team names required.
    const blank = sortedLive.find((r) => !r.teamName.trim());
    if (blank) {
      message.error('Una o piu\' squadre sono senza nome. Compila prima di salvare.');
      return;
    }
    const payload = toReplacePayload(sortedLive);
    try {
      await replaceMutation.mutateAsync(payload);
      message.success(`Classifica salvata (${payload.rows.length} righe)`);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore salvataggio');
    }
  }, [buffer, replaceMutation, tieBreakMode]);

  // --- Sync da fonti esterne (Tuttocampo) ---
  const onSyncExternal = useCallback(async () => {
    try {
      const res = await syncMutation.mutateAsync();
      message.success(`Classifica aggiornata da Tuttocampo (${res.length} squadre)`);
    } catch (e) {
      message.error(`${syncErrorMessage(e)} — classifica precedente conservata.`);
      setScreenshotOpen(true);
    }
  }, [syncMutation]);

  const onDiscard = useCallback(() => {
    buffer.discardAll();
    setDiscardConfirm(false);
    setEditing(null);
  }, [buffer]);

  // beforeunload guard: warn operator if leaving with dirty buffer.
  useEffect(() => {
    if (!buffer.isDirty) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [buffer.isDirty]);

  // --- Keyboard handler (top-level, fires when no input is active editing) ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Ctrl+S / Ctrl+D always active regardless of editing state.
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        void onSave();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        if (buffer.isDirty) setDiscardConfirm(true);
        return;
      }
      // Guard: when an input/textarea is the actual target, leave its native behavior
      // alone (typing in Comp/Stag fields, search, etc.). Edit-cell InputNumbers handle
      // their own Esc/Enter/Tab via dedicated onKeyDown.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === '?' && !editing) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (!focus) return;
      const draft = draftByUid.get(focus.uid);
      if (!draft) return;

      // Movement keys (only when not editing).
      if (!editing) {
        if (e.key === 'ArrowRight' || e.key === 'Tab') {
          if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            const n = nextFocus(focus, 'left', buffer.visible);
            if (n) setFocus(n);
            return;
          }
          e.preventDefault();
          const n = nextFocus(focus, 'right', buffer.visible);
          if (n) setFocus(n);
          return;
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const n = nextFocus(focus, 'left', buffer.visible);
          if (n) setFocus(n);
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const n = nextFocus(focus, 'down', buffer.visible);
          if (n) setFocus(n);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const n = nextFocus(focus, 'up', buffer.visible);
          if (n) setFocus(n);
          return;
        }
        if (e.key === 'Enter' || e.key === 'F2') {
          e.preventDefault();
          setEditing(focus);
          return;
        }
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          const delta = e.shiftKey ? 5 : 1;
          const cur = draft[focus.col] as number;
          buffer.patchRow(focus.uid, { [focus.col]: Math.max(0, cur + delta) } as Partial<DraftRow>);
          return;
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          const delta = e.shiftKey ? 5 : 1;
          const cur = draft[focus.col] as number;
          buffer.patchRow(focus.uid, { [focus.col]: Math.max(0, cur - delta) } as Partial<DraftRow>);
          return;
        }
        if (/^[0-9]$/.test(e.key)) {
          e.preventDefault();
          buffer.patchRow(focus.uid, { [focus.col]: Number(e.key) } as Partial<DraftRow>);
          setEditing(focus);
          return;
        }
        if (e.key === 'Insert') {
          e.preventDefault();
          const uid = buffer.addRow();
          setFocus({ uid, col: 'played' });
          return;
        }
        if (e.key === 'Delete') {
          e.preventDefault();
          if (window.confirm(`Eliminare "${draft.teamName}" dalla classifica?`)) {
            buffer.deleteRow(focus.uid);
            setFocus(null);
          }
          return;
        }
        if (e.key === 'g' || e.key === 'G') {
          e.preventDefault();
          setQuickAnchorUid(focus.uid);
          return;
        }
        if (e.key === 'l' && !e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          buffer.patchRow(focus.uid, { pointsLocked: !draft.pointsLocked });
          return;
        }
        if ((e.key === 'l' || e.key === 'L') && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          setLogoForUid(focus.uid);
          return;
        }
        if (e.key === 't' || e.key === 'T') {
          e.preventDefault();
          buffer.setTenant(focus.uid);
          return;
        }
      }
    },
    [buffer, draftByUid, editing, focus, onSave],
  );

  const onCellClick = (uid: string, col: ColKey): void => {
    setFocus({ uid, col });
    setEditing({ uid, col });
  };

  const onCellBlur = (): void => {
    setEditing(null);
    // Return focus to the wrapper so arrow-nav resumes working without a click.
    setTimeout(() => wrapperRef.current?.focus(), 0);
  };

  // --- Quick result confirm ---
  const onQuickConfirm = useCallback(
    (opponent: DraftRow, tenantGoals: number, opponentGoals: number) => {
      const tenant = tenantRow;
      if (!tenant) return;
      const result =
        tenantGoals > opponentGoals ? 'W' : tenantGoals === opponentGoals ? 'D' : 'L';
      buffer.patchRow(tenant.uid, {
        played: tenant.played + 1,
        won: tenant.won + (result === 'W' ? 1 : 0),
        drawn: tenant.drawn + (result === 'D' ? 1 : 0),
        lost: tenant.lost + (result === 'L' ? 1 : 0),
        goalsFor: tenant.goalsFor + tenantGoals,
        goalsAgainst: tenant.goalsAgainst + opponentGoals,
      });
      buffer.patchRow(opponent.uid, {
        played: opponent.played + 1,
        won: opponent.won + (result === 'L' ? 1 : 0),
        drawn: opponent.drawn + (result === 'D' ? 1 : 0),
        lost: opponent.lost + (result === 'W' ? 1 : 0),
        goalsFor: opponent.goalsFor + opponentGoals,
        goalsAgainst: opponent.goalsAgainst + tenantGoals,
      });
      message.success(`Risultato registrato: ${tenant.teamName} ${tenantGoals}-${opponentGoals} ${opponent.teamName}`);
    },
    [buffer, tenantRow],
  );

  // --- Render helpers ---
  const renderNumericCell = (row: DraftRow, col: ColKey): React.ReactNode => {
    const value = row[col] as number;
    const isFocused = focus?.uid === row.uid && focus.col === col;
    const isEditing = editing?.uid === row.uid && editing.col === col;
    const isDirty = buffer.dirtyUids.has(row.uid);
    const isPtAuto = col === 'points' && !row.pointsLocked;

    if (col === 'points' && !row.pointsLocked) {
      // Auto net Pt: rendered read-only with asterisk if penalty>0.
      return (
        <Typography.Text
          strong
          style={{
            color: row.penalty > 0 ? '#cf1322' : undefined,
            fontVariantNumeric: 'tabular-nums',
            cursor: 'default',
          }}
        >
          {value}
          {row.penalty > 0 ? <Tooltip title={`Punti netti dopo −${row.penalty} di penalizzazione`}><sup>*</sup></Tooltip> : null}
        </Typography.Text>
      );
    }

    if (isEditing) {
      return (
        <InputNumber
          size="small"
          autoFocus
          min={0}
          max={999}
          value={value}
          onChange={(v) => buffer.patchRow(row.uid, { [col]: Number(v ?? 0) } as Partial<DraftRow>)}
          onPressEnter={() => {
            setEditing(null);
            const n = nextFocus({ uid: row.uid, col }, 'down', buffer.visible);
            if (n) setFocus(n);
          }}
          onBlur={onCellBlur}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(null);
            if (e.key === 'Tab') {
              e.preventDefault();
              setEditing(null);
              const n = nextFocus({ uid: row.uid, col }, e.shiftKey ? 'left' : 'right', buffer.visible);
              if (n) setFocus(n);
            }
          }}
          style={{ width: 64 }}
        />
      );
    }
    return (
      <span
        role="button"
        tabIndex={-1}
        onClick={() => onCellClick(row.uid, col)}
        style={{
          display: 'inline-block',
          minWidth: 32,
          padding: '2px 6px',
          textAlign: 'center',
          borderRadius: 4,
          fontVariantNumeric: 'tabular-nums',
          background: isFocused ? '#e6f4ff' : isDirty ? '#fff7e6' : 'transparent',
          border: isFocused ? '1px solid #1677ff' : '1px solid transparent',
          cursor: 'text',
          opacity: isPtAuto ? 0.6 : 1,
        }}
      >
        {value}
      </span>
    );
  };

  const columns: TableColumnsType<DraftRow> = [
    {
      title: '#',
      width: 50,
      align: 'center',
      render: (_v, _r, idx) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{idx + 1}</span>,
    },
    {
      title: 'Squadra',
      render: (_v, r) => {
        const isTied = tiedRowUids.has(r.uid);
        const isEditing = editing?.uid === r.uid && editing.col === ('teamName' as ColKey);
        return (
          <Space size={6}>
            <Tooltip title="Clicca per cambiare logo (Ctrl+L)">
              <Button
                size="small"
                type="text"
                icon={r.teamLogo ? (
                  <img
                    src={r.teamLogo}
                    alt=""
                    width={20}
                    height={20}
                    style={{ objectFit: 'contain', verticalAlign: 'middle' }}
                  />
                ) : (
                  <PictureOutlined style={{ opacity: 0.4 }} />
                )}
                onClick={() => {
                  setFocus({ uid: r.uid, col: 'played' });
                  setLogoForUid(r.uid);
                }}
              />
            </Tooltip>
            {isEditing ? (
              <Input
                size="small"
                autoFocus
                defaultValue={r.teamName}
                onPressEnter={(e) => {
                  buffer.patchRow(r.uid, { teamName: (e.target as HTMLInputElement).value });
                  setEditing(null);
                }}
                onBlur={(e) => {
                  buffer.patchRow(r.uid, { teamName: e.target.value });
                  setEditing(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditing(null);
                }}
                style={{ width: 200 }}
              />
            ) : (
              <Typography.Text
                onClick={() => {
                  setFocus({ uid: r.uid, col: 'played' });
                  setEditing({ uid: r.uid, col: 'teamName' as ColKey });
                }}
                style={{ cursor: 'pointer', fontWeight: r.isOwnTeam ? 600 : 400 }}
              >
                {r.teamName || <em style={{ opacity: 0.5 }}>(senza nome)</em>}
              </Typography.Text>
            )}
            {r.isOwnTeam ? <Tag color="red">Tenant</Tag> : null}
            {r.isNew ? <Tag color="blue">Nuova</Tag> : null}
            {isTied && tieBreakMode !== 'simple' ? (
              <Tooltip title="Pari merito: scontri diretti non disponibili, applicata DR generale.">
                <InfoCircleOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
    { title: 'PG', width: 70, align: 'center', render: (_v, r) => renderNumericCell(r, 'played') },
    { title: 'V', width: 60, align: 'center', render: (_v, r) => renderNumericCell(r, 'won') },
    { title: 'N', width: 60, align: 'center', render: (_v, r) => renderNumericCell(r, 'drawn') },
    { title: 'P', width: 60, align: 'center', render: (_v, r) => renderNumericCell(r, 'lost') },
    { title: 'GF', width: 64, align: 'center', render: (_v, r) => renderNumericCell(r, 'goalsFor') },
    { title: 'GS', width: 64, align: 'center', render: (_v, r) => renderNumericCell(r, 'goalsAgainst') },
    {
      title: 'DR',
      width: 70,
      align: 'center',
      render: (_v, r) => {
        const v = r.goalDifference;
        const color = v > 0 ? 'green' : v < 0 ? 'red' : undefined;
        const text = v > 0 ? `+${v}` : `${v}`;
        return <Tag color={color} style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</Tag>;
      },
    },
    { title: 'PEN', width: 60, align: 'center', render: (_v, r) => renderNumericCell(r, 'penalty') },
    {
      title: (
        <Space size={4}>
          <span>Pt</span>
          <Tooltip title="Punti netti = 3·V + N − PEN. Lock per riga per override manuale.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      ),
      width: 80,
      align: 'center',
      render: (_v, r) => renderNumericCell(r, 'points'),
    },
    {
      title: '',
      width: 130,
      align: 'right',
      render: (_v, r) => (
        <Space size={2}>
          <Tooltip title={r.pointsLocked ? 'Pt manuale (L)' : 'Pt auto-calcolato (L)'}>
            <Button
              size="small"
              type="text"
              icon={r.pointsLocked ? <LockOutlined /> : <UnlockOutlined />}
              onClick={() => buffer.patchRow(r.uid, { pointsLocked: !r.pointsLocked })}
            />
          </Tooltip>
          <Tooltip title={r.isOwnTeam ? 'Tenant (T per cambiare)' : 'Imposta come Tenant (T)'}>
            <Button
              size="small"
              type="text"
              icon={r.isOwnTeam ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => buffer.setTenant(r.uid)}
            />
          </Tooltip>
          <Popconfirm
            title={`Eliminare "${r.teamName || 'questa riga'}"?`}
            okText="Elimina"
            cancelText="Annulla"
            okButtonProps={{ danger: true }}
            onConfirm={() => buffer.deleteRow(r.uid)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowClassName = (r: DraftRow): string => {
    const parts: string[] = [];
    if (r.isOwnTeam) parts.push('standing-own');
    if (buffer.dirtyUids.has(r.uid)) parts.push('standing-dirty');
    return parts.join(' ');
  };

  const showSimpleWarning = tieBreakMode === 'simple';

  return (
    <div ref={wrapperRef} onKeyDown={handleKeyDown} tabIndex={0} style={{ outline: 'none' }}>
      <Card
        title={
          <Space>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Classifica
            </Typography.Title>
            <Tag>{buffer.visible.length}</Tag>
          </Space>
        }
        extra={
          <Space wrap>
            <Tag style={{ padding: '4px 10px', fontSize: 13 }}>
              {competition || '—'} · {season || '—'}
            </Tag>
            <Select<TieBreakMode>
              value={tieBreakMode}
              onChange={setTieBreakMode}
              style={{ width: 240 }}
              options={[
                { value: 'simple', label: 'Semplice (punti, vittorie) — interno' },
                { value: 'figc-serie-c', label: 'FIGC Serie C / Lega Pro' },
                { value: 'figc-lnd', label: 'FIGC LND regionale' },
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching}
              disabled={buffer.isDirty}
              title={buffer.isDirty ? 'Salva o scarta le modifiche prima di ricaricare' : ''}
            >
              Aggiorna
            </Button>
            <Popconfirm
              title="Aggiornare da fonti esterne?"
              description="Sostituisce la classifica corrente con i dati di Tuttocampo."
              okText="Aggiorna"
              cancelText="Annulla"
              onConfirm={onSyncExternal}
              disabled={buffer.isDirty}
            >
              <Button
                icon={<CloudDownloadOutlined />}
                loading={syncMutation.isPending}
                disabled={buffer.isDirty}
                title={buffer.isDirty ? 'Salva o scarta le modifiche prima di sincronizzare' : ''}
              >
                Aggiorna da fonti esterne
              </Button>
            </Popconfirm>
            <Button
              icon={<CameraOutlined />}
              onClick={() => setScreenshotOpen(true)}
              disabled={buffer.isDirty}
              title={buffer.isDirty ? 'Salva o scarta le modifiche prima di importare' : ''}
            >
              Da screenshot
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                const uid = buffer.addRow();
                setFocus({ uid, col: 'played' });
              }}
            >
              Nuova riga
            </Button>
            <Tooltip title="Scorciatoie (?)">
              <Button icon={<QuestionCircleOutlined />} onClick={() => setHelpOpen(true)} />
            </Tooltip>
          </Space>
        }
      >
        {screenshotWarnings.length > 0 && buffer.isDirty ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Classifica letta da screenshot: controlla queste righe prima di salvare."
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {screenshotWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            }
            closable
            onClose={() => setScreenshotWarnings([])}
          />
        ) : null}

        {syncMutation.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="Aggiornamento da fonti esterne fallito — classifica precedente conservata."
            description={
              <span>
                {syncErrorMessage(syncMutation.error)}. {formatLastSynced(lastSyncedAt)}.
              </span>
            }
            closable
            onClose={() => syncMutation.reset()}
          />
        ) : null}

        {showSimpleWarning ? (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 12 }}
            message="Vista interna — ordine non conforme a regolamento FIGC."
            description={
              <span>
                La modalita&apos; {'{'}punti, vittorie{'}'} produce posizioni difformi dal Comunicato Ufficiale quando ci sono squadre a pari punti.
                Non esporre questa classifica al pubblico.
              </span>
            }
            action={
              <Space direction="vertical">
                <Button size="small" onClick={() => setTieBreakMode('figc-serie-c')}>
                  Serie C
                </Button>
                <Button size="small" onClick={() => setTieBreakMode('figc-lnd')}>
                  LND
                </Button>
              </Space>
            }
          />
        ) : null}

        {buffer.visible.length === 0 && !isLoading ? (
          <Empty
            description={`Nessuna classifica per ${competition} · ${season}`}
            style={{ padding: '40px 0' }}
          >
            <Space direction="vertical" size={8}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  const uid = buffer.addRow({ teamName: clubName, isOwnTeam: true });
                  setFocus({ uid, col: 'played' });
                }}
              >
                Crea classifica con {clubName}
              </Button>
            </Space>
          </Empty>
        ) : (
          <Table<DraftRow>
            rowKey="uid"
            size="small"
            loading={isLoading}
            dataSource={sortedRows}
            pagination={false}
            scroll={{ x: 'max-content' }}
            rowClassName={rowClassName}
            columns={columns}
            onRow={(r) => ({
              onClick: () => {
                if (!focus || focus.uid !== r.uid) setFocus({ uid: r.uid, col: 'played' });
              },
            })}
          />
        )}

        <Space wrap size={16} style={{ marginTop: 16 }}>
          {STANDINGS_LEGEND.map((z) => (
            <Space key={z.label} size={6} align="center">
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  background: z.color,
                }}
              />
              <Typography.Text style={{ fontSize: 12 }}>{z.label}</Typography.Text>
            </Space>
          ))}
        </Space>

        <Typography.Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>
          {formatLastSynced(lastSyncedAt)}. Pt = 3·V + N − PEN (auto). DR = GF − GS (auto). Premi{' '}
          <Tag>?</Tag> per le scorciatoie.
        </Typography.Paragraph>
      </Card>

      {buffer.isDirty ? (
        <Affix offsetBottom={0}>
          <div
            style={{
              background: '#fffbe6',
              borderTop: '1px solid #ffe58f',
              padding: '12px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <Typography.Text strong>
              {buffer.dirtyCount === 1
                ? '1 modifica in sospeso'
                : `${buffer.dirtyCount} modifiche in sospeso`}
            </Typography.Text>
            <Space>
              <Button icon={<UndoOutlined />} onClick={() => setDiscardConfirm(true)}>
                Scarta tutto
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={replaceMutation.isPending}
                onClick={onSave}
              >
                Salva (Ctrl+S)
              </Button>
            </Space>
          </div>
        </Affix>
      ) : null}

      <Modal
        open={discardConfirm}
        title="Scartare tutte le modifiche?"
        okText="Scarta"
        okButtonProps={{ danger: true }}
        cancelText="Annulla"
        onCancel={() => setDiscardConfirm(false)}
        onOk={onDiscard}
      >
        Le {buffer.dirtyCount} modifiche in sospeso saranno perse.
      </Modal>

      <Modal
        open={logoForUid !== null}
        title="Logo squadra"
        footer={null}
        onCancel={() => setLogoForUid(null)}
        destroyOnHidden
      >
        {logoForUid != null ? (
          <MediaSelector
            category="TEAM_LOGO"
            value={draftByUid.get(logoForUid)?.teamLogo ?? null}
            onChange={(url) => {
              buffer.patchRow(logoForUid, { teamLogo: url });
              setLogoForUid(null);
            }}
          />
        ) : null}
      </Modal>

      <QuickResultPopover
        open={quickAnchorUid != null}
        onClose={() => setQuickAnchorUid(null)}
        anchorRow={quickAnchorDraft}
        tenantRow={tenantRow}
        allRows={buffer.visible}
        onConfirm={onQuickConfirm}
      />

      <ShortcutsHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Modal
        open={screenshotOpen}
        title="Classifica da screenshot"
        footer={null}
        onCancel={() => setScreenshotOpen(false)}
        destroyOnHidden
      >
        <div
          onPaste={(e) => {
            const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
            if (file) void onScreenshot(file);
          }}
        >
          <Typography.Paragraph type="secondary">
            Fai uno screenshot della tabella Tuttocampo e incollalo qui (Ctrl/Cmd+V) oppure scegli il file. Le righe
            lette finiscono in bozza: nulla viene salvato finché non premi Salva.
          </Typography.Paragraph>
          <Input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={screenshotMutation.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onScreenshot(file);
            }}
          />
          {screenshotMutation.isPending ? (
            <Typography.Paragraph style={{ marginTop: 12 }}>Lettura in corso…</Typography.Paragraph>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
